'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useRef } from 'react';
import { FeatherDownload, FeatherX } from '@subframe/core';
import { Button, Drawer } from '@/shared/components/ui';
import { formatDateTimeFull } from '@/shared/formatters';
import type { ArquivoDoPacote, ChaveDaSecao, EtapaGeracao, FormatoDownload, PacoteDosAutos, PacoteListado, PortaDosAutos, RegistroDeAnexos, SecaoDosAutos } from '../types/baixarProcesso.types';
import { arquivoPrincipalDo, descricaoDosArquivos, formatarBytes, LIMITE_POR_PARTE_MB, tamanhoDoDownload } from '../utils/arquivosDoPacote';
import { esperandoPacote, PRAZO_DA_DECISAO_MS } from '../utils/esperaDoPacote';
import { ICONE_POR_FORMATO, opcaoDoFormato } from '../utils/formatosDownload';
import { rotuloDoStatus } from '../utils/passosGeracao';
import { EtapaEscolhaFormato } from './drawer/EtapaEscolhaFormato';
import { EtapaFalhaGeracao } from './drawer/EtapaFalhaGeracao';
import { EtapaGerando } from './drawer/EtapaGerando';
import { EtapaPacotePronto } from './drawer/EtapaPacotePronto';
import { EtapaSegundoPlano } from './drawer/EtapaSegundoPlano';
import { EtapaSelecaoSecoes } from './drawer/EtapaSelecaoSecoes';
import { EtapaSemAcesso } from './drawer/EtapaSemAcesso';
const ID_MOTIVO_DO_BLOQUEIO = 'baixar-processo-motivo-do-bloqueio';
interface BaixarProcessoDrawerProps {
  aberto: boolean;
  porta: PortaDosAutos;
  numeroProcesso: string;
  /** `null` enquanto o formato é escolhido dentro do painel. */
  formato: FormatoDownload | null;
  formatoEmEscolha: FormatoDownload | null;
  etapa: EtapaGeracao;
  /** Seções do servidor; `null` enquanto não chegaram. */
  secoes: readonly SecaoDosAutos[] | null;
  carregandoSecoes: boolean;
  secoesMarcadas: readonly ChaveDaSecao[];
  avisoDaSelecao: string | null;
  /** Pacote pronto da versão atual com exatamente esta seleção: o download sai na hora. */
  pacoteJaGerado: PacoteListado | null;
  /**
   * Contagem da pergunta "continuar esperando?"; `null` quando não há decisão aberta — fora da
   * pergunta, ou com o prazo vencido e o e-mail já pedido.
   */
  segundosParaDecidir: number | null;
  pacote: PacoteDosAutos | null;
  reaproveitado: boolean;
  erroGeracao: string | null;
  /** A falha foi o documento passar do limite: tentar de novo não resolve, um recorte sim. */
  falhouPorLimite: boolean;
  arquivoEmPreparo: ArquivoDoPacote | null;
  /** O registro de anexos do pacote pronto, quando há o que mostrar (ver `precisaDoRegistro`). */
  registroDeAnexos: RegistroDeAnexos | null;
  carregandoRegistro: boolean;
  falhaNoRegistro: boolean;
  enviandoCiencia: boolean;
  emailDeAviso: string;
  /** O gatilho que abriu o painel: o foco volta para ele ao fechar. */
  retornoDoFoco: RefObject<HTMLElement | null>;
  onFechar: () => void;
  onTrocarFormato: () => void;
  onMarcarFormato: (formato: FormatoDownload) => void;
  onContinuarComFormato: () => void;
  onAlternarSecao: (chave: ChaveDaSecao) => void;
  onAlternarTodas: () => void;
  onGerar: () => void;
  onTentarNovamente: () => void;
  /** Depois da falha por limite: vai para a marcação de seções. */
  onEscolherSecoes: () => void;
  onCancelarGeracao: () => void;
  onVoltarParaSelecao: () => void;
  onBaixarArquivo: (arquivo: ArquivoDoPacote) => void;
  onContinuarEsperando: () => void;
  /** "Estou ciente" dos anexos que não entraram no PDF: libera o download no servidor. */
  onDarCiencia: () => void;
  onRecarregarRegistro: () => void;
}
export function BaixarProcessoDrawer(t0) {
  const $ = _c(168);
  const {
    aberto,
    porta,
    numeroProcesso,
    formato,
    formatoEmEscolha,
    etapa,
    secoes,
    carregandoSecoes,
    secoesMarcadas,
    avisoDaSelecao,
    pacoteJaGerado,
    segundosParaDecidir,
    pacote,
    reaproveitado,
    erroGeracao,
    falhouPorLimite,
    arquivoEmPreparo,
    registroDeAnexos,
    carregandoRegistro,
    falhaNoRegistro,
    enviandoCiencia,
    emailDeAviso,
    retornoDoFoco,
    onFechar,
    onTrocarFormato,
    onMarcarFormato,
    onContinuarComFormato,
    onAlternarSecao,
    onAlternarTodas,
    onGerar,
    onTentarNovamente,
    onEscolherSecoes,
    onCancelarGeracao,
    onVoltarParaSelecao,
    onBaixarArquivo,
    onContinuarEsperando,
    onDarCiencia,
    onRecarregarRegistro
  } = t0;
  const titulo = useRef(null);
  let t1;
  if ($[0] !== carregandoSecoes || $[1] !== etapa || $[2] !== formatoEmEscolha || $[3] !== secoesMarcadas) {
    t1 = bloqueioDaConfirmacao({
      etapa,
      formatoEmEscolha,
      carregandoSecoes,
      secoesMarcadas
    });
    $[0] = carregandoSecoes;
    $[1] = etapa;
    $[2] = formatoEmEscolha;
    $[3] = secoesMarcadas;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const motivoDoBloqueio = t1;
  const pacotePronto = etapa === "pronto" && pacote !== null && formato !== null ? pacote : null;
  let T0;
  let T1;
  let arquivoPrincipal;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== aberto || $[6] !== etapa || $[7] !== formato || $[8] !== numeroProcesso || $[9] !== onFechar || $[10] !== onTrocarFormato || $[11] !== pacote || $[12] !== pacotePronto || $[13] !== retornoDoFoco.current) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const tamanhoPronto = pacotePronto && formato ? tamanhoDoDownload(formato, pacotePronto) : null;
      let t12;
      if ($[27] !== formato || $[28] !== pacotePronto) {
        t12 = arquivoPrincipalDo(formato, pacotePronto);
        $[27] = formato;
        $[28] = pacotePronto;
        $[29] = t12;
      } else {
        t12 = $[29];
      }
      arquivoPrincipal = t12;
      let t13;
      if ($[30] !== retornoDoFoco.current) {
        t13 = evento => {
          evento.preventDefault();
          const gatilho = retornoDoFoco.current;
          if (!gatilho || gatilho.getAttribute("aria-expanded") === "true") {
            return;
          }
          gatilho.focus();
        };
        $[30] = retornoDoFoco.current;
        $[31] = t13;
      } else {
        t13 = $[31];
      }
      const devolverFoco = t13;
      if (!aberto) {
        t11 = null;
        break bb0;
      }
      T1 = Drawer;
      if ($[32] !== onFechar) {
        t8 = proximo => !proximo && onFechar();
        $[32] = onFechar;
        $[33] = t8;
      } else {
        t8 = $[33];
      }
      t9 = true;
      t10 = "Baixar processo";
      T0 = Drawer.Content;
      t5 = "w-full max-w-[520px] gap-0 border-l-0";
      t6 = devolverFoco;
      if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = evento_0 => {
          evento_0.preventDefault();
          titulo.current?.focus();
        };
        $[34] = t7;
      } else {
        t7 = $[34];
      }
      t2 = "flex w-full flex-col gap-3.5 border-b border-solid border-neutral-100 px-6 pt-[22px] pb-4";
      let t14;
      if ($[35] !== numeroProcesso) {
        t14 = <span className="text-[10.5px] font-semibold tracking-[0.05em] text-neutral-400 uppercase">{numeroProcesso}</span>;
        $[35] = numeroProcesso;
        $[36] = t14;
      } else {
        t14 = $[36];
      }
      let t15;
      if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = <h2 className="text-default-font m-0 text-[20px] leading-6 font-semibold outline-none" ref={titulo} tabIndex={-1}>Baixar processo</h2>;
        $[37] = t15;
      } else {
        t15 = $[37];
      }
      let t16;
      if ($[38] !== etapa || $[39] !== formato || $[40] !== pacote) {
        t16 = subtituloDaEtapa({
          etapa,
          formato,
          pacote
        });
        $[38] = etapa;
        $[39] = formato;
        $[40] = pacote;
        $[41] = t16;
      } else {
        t16 = $[41];
      }
      let t17;
      if ($[42] !== t16) {
        t17 = <p aria-live="polite" className="m-0 text-[13.5px] leading-5 text-neutral-500">{t16}</p>;
        $[42] = t16;
        $[43] = t17;
      } else {
        t17 = $[43];
      }
      const t18 = etapa === "aguardandoDecisao" ? `A geração está demorando. Para continuar esperando, use o botão Continuar esperando; sem resposta em ${PRAZO_DA_DECISAO_MS / 1000} segundos, avisamos por e-mail quando terminar.` : "";
      let t19;
      if ($[44] !== t18) {
        t19 = <p aria-live="polite" className="sr-only">{t18}</p>;
        $[44] = t18;
        $[45] = t19;
      } else {
        t19 = $[45];
      }
      let t20;
      if ($[46] !== t14 || $[47] !== t17 || $[48] !== t19) {
        t20 = <div className="flex flex-col gap-[3px]">{t14}{t15}{t17}{t19}</div>;
        $[46] = t14;
        $[47] = t17;
        $[48] = t19;
        $[49] = t20;
      } else {
        t20 = $[49];
      }
      let t21;
      if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = <FeatherX className="h-4 w-4" />;
        $[50] = t21;
      } else {
        t21 = $[50];
      }
      let t22;
      if ($[51] !== onFechar) {
        t22 = <button aria-label="Fechar" className="flex h-8 w-8 flex-none items-center justify-center rounded-[8px] text-neutral-500 hover:bg-neutral-100" onClick={onFechar} type="button">{t21}</button>;
        $[51] = onFechar;
        $[52] = t22;
      } else {
        t22 = $[52];
      }
      if ($[53] !== t20 || $[54] !== t22) {
        t3 = <div className="flex items-start justify-between gap-4">{t20}{t22}</div>;
        $[53] = t20;
        $[54] = t22;
        $[55] = t3;
      } else {
        t3 = $[55];
      }
      t4 = formato ? <div className="bg-brand-50 flex items-center gap-3 rounded-[12px] px-3.5 py-[11px]"><span className="text-brand-600 flex-none">{ICONE_POR_FORMATO[formato]}</span><div className="flex min-w-0 flex-1 flex-col gap-px"><span className="text-default-font text-[13px] font-medium">{opcaoDoFormato(formato).titulo}</span><span className="text-[11.5px] text-neutral-500">{descricaoDosArquivos(formato, pacotePronto)}{tamanhoPronto !== null ? ` · ${formatarBytes(tamanhoPronto)}` : null}</span></div><button className="text-brand-600 flex-none text-[12.5px] font-medium hover:underline" onClick={onTrocarFormato} type="button">Trocar formato</button></div> : null;
    }
    $[5] = aberto;
    $[6] = etapa;
    $[7] = formato;
    $[8] = numeroProcesso;
    $[9] = onFechar;
    $[10] = onTrocarFormato;
    $[11] = pacote;
    $[12] = pacotePronto;
    $[13] = retornoDoFoco.current;
    $[14] = T0;
    $[15] = T1;
    $[16] = arquivoPrincipal;
    $[17] = t10;
    $[18] = t11;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    T0 = $[14];
    T1 = $[15];
    arquivoPrincipal = $[16];
    t10 = $[17];
    t11 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  let t12;
  if ($[56] !== t2 || $[57] !== t3 || $[58] !== t4) {
    t12 = <div className={t2}>{t3}{t4}</div>;
    $[56] = t2;
    $[57] = t3;
    $[58] = t4;
    $[59] = t12;
  } else {
    t12 = $[59];
  }
  let t13;
  if ($[60] !== etapa || $[61] !== formatoEmEscolha || $[62] !== onMarcarFormato) {
    t13 = etapa === "formato" && <EtapaEscolhaFormato formatoEmEscolha={formatoEmEscolha} onMarcar={onMarcarFormato} />;
    $[60] = etapa;
    $[61] = formatoEmEscolha;
    $[62] = onMarcarFormato;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== avisoDaSelecao || $[65] !== carregandoSecoes || $[66] !== etapa || $[67] !== formato || $[68] !== onAlternarSecao || $[69] !== onAlternarTodas || $[70] !== pacoteJaGerado || $[71] !== secoes || $[72] !== secoesMarcadas) {
    t14 = etapa === "selecao" && formato === "secoes" && <EtapaSelecaoSecoes aviso={avisoDaSelecao} carregando={carregandoSecoes || secoes === null} onAlternarSecao={onAlternarSecao} onAlternarTodas={onAlternarTodas} pacoteJaGerado={pacoteJaGerado} secoes={secoes ?? []} secoesMarcadas={secoesMarcadas} />;
    $[64] = avisoDaSelecao;
    $[65] = carregandoSecoes;
    $[66] = etapa;
    $[67] = formato;
    $[68] = onAlternarSecao;
    $[69] = onAlternarTodas;
    $[70] = pacoteJaGerado;
    $[71] = secoes;
    $[72] = secoesMarcadas;
    $[73] = t14;
  } else {
    t14 = $[73];
  }
  let t15;
  if ($[74] !== etapa || $[75] !== formato || $[76] !== pacote?.status) {
    t15 = esperandoPacote(etapa) && formato && <EtapaGerando formato={formato} status={pacote?.status ?? null} />;
    $[74] = etapa;
    $[75] = formato;
    $[76] = pacote?.status;
    $[77] = t15;
  } else {
    t15 = $[77];
  }
  let t16;
  if ($[78] !== arquivoEmPreparo || $[79] !== carregandoRegistro || $[80] !== enviandoCiencia || $[81] !== falhaNoRegistro || $[82] !== formato || $[83] !== numeroProcesso || $[84] !== onBaixarArquivo || $[85] !== onDarCiencia || $[86] !== onRecarregarRegistro || $[87] !== pacotePronto || $[88] !== porta || $[89] !== reaproveitado || $[90] !== registroDeAnexos) {
    t16 = pacotePronto && formato && <EtapaPacotePronto arquivoEmPreparo={arquivoEmPreparo} carregandoRegistro={carregandoRegistro} enviandoCiencia={enviandoCiencia} falhaNoRegistro={falhaNoRegistro} formato={formato} numeroProcesso={numeroProcesso} onBaixarArquivo={onBaixarArquivo} onDarCiencia={onDarCiencia} onRecarregarRegistro={onRecarregarRegistro} pacote={pacotePronto} porta={porta} reaproveitado={reaproveitado} registro={registroDeAnexos} />;
    $[78] = arquivoEmPreparo;
    $[79] = carregandoRegistro;
    $[80] = enviandoCiencia;
    $[81] = falhaNoRegistro;
    $[82] = formato;
    $[83] = numeroProcesso;
    $[84] = onBaixarArquivo;
    $[85] = onDarCiencia;
    $[86] = onRecarregarRegistro;
    $[87] = pacotePronto;
    $[88] = porta;
    $[89] = reaproveitado;
    $[90] = registroDeAnexos;
    $[91] = t16;
  } else {
    t16 = $[91];
  }
  let t17;
  if ($[92] !== emailDeAviso || $[93] !== etapa || $[94] !== formato) {
    t17 = etapa === "segundoPlano" && <EtapaSegundoPlano emailDeAviso={emailDeAviso} emPartes={formato === "partes"} />;
    $[92] = emailDeAviso;
    $[93] = etapa;
    $[94] = formato;
    $[95] = t17;
  } else {
    t17 = $[95];
  }
  let t18;
  if ($[96] !== erroGeracao || $[97] !== etapa || $[98] !== falhouPorLimite || $[99] !== formato) {
    t18 = etapa === "falha" && <EtapaFalhaGeracao erroGeracao={erroGeracao} falhouPorLimite={falhouPorLimite} formato={formato} />;
    $[96] = erroGeracao;
    $[97] = etapa;
    $[98] = falhouPorLimite;
    $[99] = formato;
    $[100] = t18;
  } else {
    t18 = $[100];
  }
  let t19;
  if ($[101] !== etapa || $[102] !== porta) {
    t19 = (etapa === "semPermissao" || etapa === "indisponivel") && <EtapaSemAcesso motivo={etapa} porta={porta} />;
    $[101] = etapa;
    $[102] = porta;
    $[103] = t19;
  } else {
    t19 = $[103];
  }
  let t20;
  if ($[104] !== etapa || $[105] !== motivoDoBloqueio || $[106] !== onContinuarComFormato || $[107] !== onFechar || $[108] !== onGerar) {
    t20 = (etapa === "formato" || etapa === "selecao") && <div className="flex w-full items-center justify-end gap-3">{motivoDoBloqueio ? <span className="mr-auto text-[12px] leading-[1.5] text-neutral-500" id={ID_MOTIVO_DO_BLOQUEIO}>{motivoDoBloqueio}</span> : null}<Button onClick={onFechar} variant="neutral-secondary">Cancelar</Button>{etapa === "formato" ? <Button aria-describedby={motivoDoBloqueio ? ID_MOTIVO_DO_BLOQUEIO : undefined} disabled={motivoDoBloqueio !== null} onClick={onContinuarComFormato} variant="brand-primary">Continuar</Button> : <Button aria-describedby={motivoDoBloqueio ? ID_MOTIVO_DO_BLOQUEIO : undefined} disabled={motivoDoBloqueio !== null} icon={<FeatherDownload />} onClick={onGerar} variant="brand-primary">Baixar</Button>}</div>;
    $[104] = etapa;
    $[105] = motivoDoBloqueio;
    $[106] = onContinuarComFormato;
    $[107] = onFechar;
    $[108] = onGerar;
    $[109] = t20;
  } else {
    t20 = $[109];
  }
  let t21;
  if ($[110] !== etapa || $[111] !== onCancelarGeracao) {
    t21 = etapa === "gerando" && <div className="flex w-full items-center justify-end"><Button onClick={onCancelarGeracao} variant="neutral-secondary">Cancelar geração</Button></div>;
    $[110] = etapa;
    $[111] = onCancelarGeracao;
    $[112] = t21;
  } else {
    t21 = $[112];
  }
  let t22;
  if ($[113] !== etapa || $[114] !== onCancelarGeracao || $[115] !== onContinuarEsperando || $[116] !== segundosParaDecidir) {
    t22 = etapa === "aguardandoDecisao" && <div className="flex w-full items-center justify-end gap-3"><Button onClick={onCancelarGeracao} variant="neutral-secondary">Cancelar geração</Button><Button aria-label="Continuar esperando" disabled={segundosParaDecidir === null} onClick={onContinuarEsperando} variant="brand-primary">Continuar esperando ({segundosParaDecidir ?? 0}s)</Button></div>;
    $[113] = etapa;
    $[114] = onCancelarGeracao;
    $[115] = onContinuarEsperando;
    $[116] = segundosParaDecidir;
    $[117] = t22;
  } else {
    t22 = $[117];
  }
  let t23;
  if ($[118] !== arquivoEmPreparo || $[119] !== arquivoPrincipal || $[120] !== etapa || $[121] !== formato || $[122] !== onBaixarArquivo || $[123] !== onFechar || $[124] !== onVoltarParaSelecao || $[125] !== pacotePronto?.cienciaPendente) {
    t23 = etapa === "pronto" && <>{formato === "secoes" ? <button className="text-brand-600 text-[13px] font-medium hover:underline" onClick={onVoltarParaSelecao} type="button">Mudar a seleção</button> : <span />}<div className="flex items-center gap-3"><Button onClick={onFechar} variant="neutral-secondary">Fechar</Button><Button disabled={arquivoEmPreparo !== null || pacotePronto?.cienciaPendente === true} icon={<FeatherDownload />} loading={arquivoEmPreparo === arquivoPrincipal} onClick={() => onBaixarArquivo(arquivoPrincipal)} variant="brand-primary">{arquivoPrincipal === "zip" ? "Baixar tudo (.zip)" : "Baixar PDF"}</Button></div></>;
    $[118] = arquivoEmPreparo;
    $[119] = arquivoPrincipal;
    $[120] = etapa;
    $[121] = formato;
    $[122] = onBaixarArquivo;
    $[123] = onFechar;
    $[124] = onVoltarParaSelecao;
    $[125] = pacotePronto?.cienciaPendente;
    $[126] = t23;
  } else {
    t23 = $[126];
  }
  let t24;
  if ($[127] !== etapa || $[128] !== onFechar) {
    t24 = etapa === "segundoPlano" && <div className="flex w-full items-center justify-end"><Button onClick={onFechar} variant="brand-primary">Entendi</Button></div>;
    $[127] = etapa;
    $[128] = onFechar;
    $[129] = t24;
  } else {
    t24 = $[129];
  }
  let t25;
  if ($[130] !== etapa || $[131] !== falhouPorLimite || $[132] !== formato || $[133] !== onEscolherSecoes || $[134] !== onTentarNovamente || $[135] !== onVoltarParaSelecao) {
    t25 = etapa === "falha" && <div className="flex w-full items-center justify-end gap-3"><BotaoDaFalha falhouPorLimite={falhouPorLimite} formato={formato} onEscolherSecoes={onEscolherSecoes} onTentarNovamente={onTentarNovamente} onVoltarParaSelecao={onVoltarParaSelecao} /></div>;
    $[130] = etapa;
    $[131] = falhouPorLimite;
    $[132] = formato;
    $[133] = onEscolherSecoes;
    $[134] = onTentarNovamente;
    $[135] = onVoltarParaSelecao;
    $[136] = t25;
  } else {
    t25 = $[136];
  }
  let t26;
  if ($[137] !== etapa || $[138] !== onFechar) {
    t26 = (etapa === "semPermissao" || etapa === "indisponivel") && <div className="flex w-full items-center justify-end"><Button onClick={onFechar} variant="neutral-secondary">Fechar</Button></div>;
    $[137] = etapa;
    $[138] = onFechar;
    $[139] = t26;
  } else {
    t26 = $[139];
  }
  let t27;
  if ($[140] !== t20 || $[141] !== t21 || $[142] !== t22 || $[143] !== t23 || $[144] !== t24 || $[145] !== t25 || $[146] !== t26) {
    t27 = <div className="mt-auto flex w-full items-center justify-between gap-3 border-t border-solid border-neutral-100 px-6 py-4">{t20}{t21}{t22}{t23}{t24}{t25}{t26}</div>;
    $[140] = t20;
    $[141] = t21;
    $[142] = t22;
    $[143] = t23;
    $[144] = t24;
    $[145] = t25;
    $[146] = t26;
    $[147] = t27;
  } else {
    t27 = $[147];
  }
  let t28;
  if ($[148] !== T0 || $[149] !== t12 || $[150] !== t13 || $[151] !== t14 || $[152] !== t15 || $[153] !== t16 || $[154] !== t17 || $[155] !== t18 || $[156] !== t19 || $[157] !== t27 || $[158] !== t5 || $[159] !== t6 || $[160] !== t7) {
    t28 = <T0 className={t5} onCloseAutoFocus={t6} onOpenAutoFocus={t7}>{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t27}</T0>;
    $[148] = T0;
    $[149] = t12;
    $[150] = t13;
    $[151] = t14;
    $[152] = t15;
    $[153] = t16;
    $[154] = t17;
    $[155] = t18;
    $[156] = t19;
    $[157] = t27;
    $[158] = t5;
    $[159] = t6;
    $[160] = t7;
    $[161] = t28;
  } else {
    t28 = $[161];
  }
  let t29;
  if ($[162] !== T1 || $[163] !== t10 || $[164] !== t28 || $[165] !== t8 || $[166] !== t9) {
    t29 = <T1 onOpenChange={t8} open={t9} title={t10}>{t28}</T1>;
    $[162] = T1;
    $[163] = t10;
    $[164] = t28;
    $[165] = t8;
    $[166] = t9;
    $[167] = t29;
  } else {
    t29 = $[167];
  }
  return t29;
}
interface BotaoDaFalhaProps {
  falhouPorLimite: boolean;
  formato: FormatoDownload | null;
  onTentarNovamente: () => void;
  onEscolherSecoes: () => void;
  onVoltarParaSelecao: () => void;
}

/**
 * A ação da falha. No limite, repetir o mesmo pedido falharia igual: o recorte já marcado volta à
 * marcação para ficar menor; o completo e o ZIP vão para a escolha de seções.
 */
function BotaoDaFalha(t0) {
  const $ = _c(6);
  const {
    falhouPorLimite,
    formato,
    onTentarNovamente,
    onEscolherSecoes,
    onVoltarParaSelecao
  } = t0;
  if (!falhouPorLimite) {
    let t1;
    if ($[0] !== onTentarNovamente) {
      t1 = <Button onClick={onTentarNovamente} variant="brand-primary">Tentar novamente</Button>;
      $[0] = onTentarNovamente;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== formato || $[3] !== onEscolherSecoes || $[4] !== onVoltarParaSelecao) {
    t1 = formato === "secoes" ? <Button onClick={onVoltarParaSelecao} variant="brand-primary">Revisar seções</Button> : <Button onClick={onEscolherSecoes} variant="brand-primary">Escolher seções</Button>;
    $[2] = formato;
    $[3] = onEscolherSecoes;
    $[4] = onVoltarParaSelecao;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
interface BloqueioInput {
  etapa: EtapaGeracao;
  formatoEmEscolha: FormatoDownload | null;
  carregandoSecoes: boolean;
  secoesMarcadas: readonly ChaveDaSecao[];
}

/** Por que o botão de avançar está desabilitado — `null` quando não está. */
function bloqueioDaConfirmacao({
  etapa,
  formatoEmEscolha,
  carregandoSecoes,
  secoesMarcadas
}: BloqueioInput): string | null {
  if (etapa === 'formato') {
    return formatoEmEscolha ? null : 'Escolha um formato para continuar.';
  }
  if (etapa !== 'selecao') return null;
  if (carregandoSecoes) return 'Carregando as seções do processo.';
  return secoesMarcadas.length === 0 ? 'Marque ao menos uma seção para baixar.' : null;
}
interface SubtituloInput {
  etapa: EtapaGeracao;
  formato: FormatoDownload | null;
  pacote: PacoteDosAutos | null;
}

/** O estado sempre em palavras: é também o que o leitor de tela anuncia a cada troca. */
function subtituloDaEtapa({
  etapa,
  formato,
  pacote
}: SubtituloInput): string {
  if (esperandoPacote(etapa)) {
    const status = pacote?.status ?? null;
    const arquivos = formato === 'partes' ? `o PDF e as partes de até ${LIMITE_POR_PARTE_MB} MB` : '1 arquivo PDF';
    if (status === 'GERANDO') return `${rotuloDoStatus(status)}: montando ${arquivos}.`;
    if (status === 'PENDENTE') return `${rotuloDoStatus(status)}: aguardando a vez de montar.`;
    return `${rotuloDoStatus(status)} do pacote.`;
  }
  if (etapa === 'pronto') {
    return pacote?.criadoEm ? `Pacote gerado em ${formatDateTimeFull(pacote.criadoEm)}.` : '';
  }
  if (etapa === 'segundoPlano') return 'Isso não sai na hora.';
  if (etapa === 'falha') return 'A montagem parou no meio. Nada foi perdido no processo.';
  if (etapa === 'semPermissao') return 'Sem permissão para baixar este processo.';
  if (etapa === 'indisponivel') return 'Download indisponível por enquanto.';
  if (etapa === 'formato') return 'Escolha como os autos vão ser baixados.';
  return 'Marque as seções que entram no PDF. O resumo se ajusta abaixo.';
}